/**
 * App composition root.
 *
 * Two boot paths for identity:
 *
 *   - mode: 'worker'  — production. Identity lives in the keyring
 *     SharedWorker; the main thread holds only a RemoteSignerIdentity.
 *
 *   - mode: 'inproc'  — testing. Identity is local to this thread, no
 *     worker. Used by vitest where SharedWorker is unavailable.
 *
 * Sync provider is independently selectable:
 *
 *   - syncBackend: 'local'    — in-memory, single-node (default)
 *   - syncBackend: 'orbitdb'  — full P2P (Peer-to-Peer) over OrbitDB + Helia
 *                               (requires the real MeshNode; TODO(beta))
 */

import { IDENTITY_MODEL, type SignerIdentity } from './identity';
import { MeshDatabase, StorageRouter } from './storage/StorageRouter';
import { LocalSyncProvider, type SyncProvider } from './sync/SyncProvider';
import { OrbitDBSyncProvider } from './sync/OrbitDBSyncProvider';
import { MockMeshNode, createRealMeshNode, type MeshNode } from './sync/MeshNode';
import { MockTransport, TransportSelector } from './transport/Transport';
import { MockDiscovery } from './discovery/Discovery';
import { SurfaceRegistry } from './surface/Surface';
import { Director } from './director/Director';
import { PlaintextGroupEncryption } from './envelope/GroupEncryption';
import { KeyringClient, RemoteSignerIdentity } from './workers/keyring.client';
import { InprocKeyringClient, InprocSignerIdentity } from './workers/keyring.inproc';
import type { KeyringClientLike } from './workers/keyring.types';

export interface AppContext {
  identity: SignerIdentity;
  storage: StorageRouter;
  sync: SyncProvider;
  meshNode: MeshNode;
  transport: TransportSelector;
  discovery: MockDiscovery;
  surfaces: SurfaceRegistry;
  director: Director;
  groupEnc: PlaintextGroupEncryption;
  identityModel: typeof IDENTITY_MODEL;
  /** Keyring client. Always present — either SharedWorker-backed or inproc. */
  keyring: KeyringClientLike;
  /** Tear down the entire stack. Called on lock or app exit. */
  shutdown(): Promise<void>;
}

export type IdentityBoot =
  | {
      mode: 'worker';
      /**
       * Either a URL/string pointing at the worker script (legacy) OR a
       * SharedWorker class constructor (the modern Vite `?sharedworker`
       * import pattern — preferred).
       */
      workerUrl: string | URL | { new(options?: WorkerOptions): SharedWorker };
      seed?: string;
      password: string;
      autoLockMs?: number;
    }
  | {
      mode: 'inproc';
      fresh: boolean;
      password: string;
      seed?: string;
    };

export type AppBootOptions = IdentityBoot & {
  /**
   * Which sync backend to use. Defaults to 'local' for safety; switching to
   * 'orbitdb' boots the real MeshNode (Helia + OrbitDB + libp2p).
   */
  syncBackend?: 'local' | 'orbitdb';
  /** Bootstrap peers for OrbitDB backend. */
  bootstrap?: string[];
};

export interface BootResult {
  ctx: AppContext;
  /** Returned when a new identity was generated. */
  seed?: string;
  fingerprint: string;
}

export async function bootApp(opts: AppBootOptions): Promise<BootResult> {
  let identity: SignerIdentity;
  let fingerprint: string;
  let returnedSeed: string | undefined;
  let keyring: KeyringClientLike;
  let envKey: CryptoKey;
  let sessKey: CryptoKey;

  // ── 1. Identity + keyring ────────────────────────────────
  // Both branches produce: identity, fingerprint, keyring, envKey, sessKey.
  // Worker mode uses SharedWorker for cross-tab state; inproc uses the same
  // keyring core in the calling thread.
  if (opts.mode === 'worker') {
    const workerClient = await KeyringClient.connect(opts.workerUrl);
    if (opts.seed) {
      const r = await workerClient.unlock(opts.seed, opts.password, opts.autoLockMs);
      if (r.kind !== 'unlock') throw new Error('Unexpected keyring response');
      fingerprint = r.fingerprint;
    } else {
      const r = await workerClient.generate(opts.password, opts.autoLockMs);
      if (r.kind !== 'generate') throw new Error('Unexpected keyring response');
      fingerprint = r.fingerprint;
      returnedSeed = r.seed;
    }
    identity = new RemoteSignerIdentity(workerClient, fingerprint);
    envKey = await workerClient.storageKey('envelope');
    sessKey = await workerClient.storageKey('session');
    keyring = workerClient;
  } else {
    const inprocClient = new InprocKeyringClient();
    if (opts.fresh) {
      const r = await inprocClient.generate(opts.password, 0); // tests disable auto-lock
      if (r.kind !== 'generate') throw new Error('Unexpected keyring response');
      fingerprint = r.fingerprint;
      returnedSeed = r.seed;
    } else {
      if (!opts.seed) throw new Error('Seed required when fresh=false');
      const r = await inprocClient.unlock(opts.seed, opts.password, 0);
      if (r.kind !== 'unlock') throw new Error('Unexpected keyring response');
      fingerprint = r.fingerprint;
    }
    identity = new InprocSignerIdentity(inprocClient, fingerprint);
    envKey = await inprocClient.storageKey('envelope');
    sessKey = await inprocClient.storageKey('session');
    keyring = inprocClient;
  }

  // ── 2. Storage ──────────────────────────────────────────
  const db = new MeshDatabase();
  await db.open();
  const storage = new StorageRouter(db);
  storage.setKeys(
    { key: envKey, domain: 'envelope' },
    { key: sessKey, domain: 'session' },
  );

  // ── 3. Mesh node + sync ─────────────────────────────────
  const syncBackend = opts.syncBackend ?? 'local';
  let meshNode: MeshNode;
  let sync: SyncProvider;
  if (syncBackend === 'orbitdb') {
    // OrbitDB owns its own sync-layer identity; we don't pass the
    // application SignerIdentity through. Per-context unlinkability is
    // preserved at the envelope layer. See docs/orbitdb-sync.md.
    const nodeOpts: Parameters<typeof createRealMeshNode>[0] = {};
    if (opts.bootstrap !== undefined) nodeOpts.bootstrap = opts.bootstrap;
    meshNode = await createRealMeshNode(nodeOpts);
    sync = new OrbitDBSyncProvider(meshNode);
  } else {
    meshNode = new MockMeshNode();
    sync = new LocalSyncProvider();
  }
  await meshNode.start();
  await sync.start();

  // ── 4. Transport + discovery (still mocks; Waku next) ──
  const transport = new TransportSelector();
  transport.register(new MockTransport());
  const discovery = new MockDiscovery();

  // ── 5. Surfaces + director ──────────────────────────────
  const surfaces = new SurfaceRegistry();
  const director = new Director(surfaces);

  // ── 6. Group encryption (stub) ──────────────────────────
  const groupEnc = new PlaintextGroupEncryption();

  // ── 7. Shutdown sequence ────────────────────────────────
  const shutdown = async (): Promise<void> => {
    await sync.stop();
    await meshNode.stop();
    await storage.lock();
  };

  // Keyring lock → cascade shutdown of sync stack
  keyring.onLock(() => { void shutdown(); });

  const ctx: AppContext = {
    identity,
    storage,
    sync,
    meshNode,
    transport,
    discovery,
    surfaces,
    director,
    groupEnc,
    identityModel: IDENTITY_MODEL,
    keyring,
    shutdown,
  };

  const result: BootResult = { ctx, fingerprint };
  if (returnedSeed !== undefined) result.seed = returnedSeed;
  return result;
}
